import { AfterViewInit, Component, effect, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { isFromMobileApp } from '@app/core/helpers';
import { setNativePlatformClasses } from './helpers/native-ios';
import { BreakpointResolutionService } from './core/services/breakpoint-resolution.service';
import { SplashScreenService } from './core/services/splash-screen.service';
import { HelpStoreActions } from './root-store/help-store';
import { RootState } from './root-store/root-state';
import { UserMeStoreActions } from './root-store/user-me-store';
import { VisibilityService } from './services/visibility-change.service';

const INFO_ICON_SELECTOR = '[class*="fa-info"], [class*="fa-question"]';

@Component({
  selector: 'by-root',
  templateUrl: './app.component.html',
  styles: [
    `
      router-outlet + .loader {
        opacity: 1;
      }

      .loader {
        opacity: 0;
        position: fixed;
        height: 100%;
        width: 100%;
        text-align: center;
        z-index: -1;
        transition: opacity 0.8s ease-out;
      }
    `,
  ],
  standalone: false,
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private subs = new SubSink();

  availableLanguages = ['it', 'en', 'es'];

  constructor(
    private router: Router,
    private store: Store<RootState>,
    private translate: TranslateService,
    private visibilityService: VisibilityService,
    private splashScreen: SplashScreenService,
    private breakpointResolution: BreakpointResolutionService,
  ) {
    // ng-zorro has no global config to disable tooltips: this class on <html>
    // lets the global style hide them on mobile (see _mobile-tooltip.global.scss)
    effect(() => {
      document.documentElement.classList.toggle(
        'is-mobile',
        this.breakpointResolution.isMobile(),
      );
    });

    // Exception for info icons: on touch, tapping the icon emulates the hover
    // that opens its tooltip, so we only re-enable tooltips while the last tap
    // was on an info icon; any other tap hides them again.
    document.addEventListener(
      'pointerdown',
      (event) => {
        const tooltipHost = (event.target as Element)?.closest?.(
          '[nz-tooltip]',
        );
        const isInfoIcon =
          !!tooltipHost &&
          (tooltipHost.matches(INFO_ICON_SELECTOR) ||
            !!tooltipHost.querySelector(INFO_ICON_SELECTOR));

        document.documentElement.classList.toggle(
          'info-tooltip-open',
          isInfoIcon,
        );
      },
      { capture: true },
    );

    let lang = (
      window.navigator['userLanguage'] || window.navigator.language
    ).slice(0, 2);

    if (!this.availableLanguages.includes(lang)) {
      lang = 'en';
    }

    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
    this.store.dispatch(UserMeStoreActions.loadRequest({ redirect: null }));

    this.subs.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe(() => {
          this.store.dispatch(HelpStoreActions.resetGuideSectionsToView());
        }),
    );

    this.visibilityService.visibilityChange();
  }

  ngAfterViewInit(): void {
    if (!isFromMobileApp()) return;

    setNativePlatformClasses();

    requestAnimationFrame(() => void this.splashScreen.hide());
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
